import {
	Column,
	ColumnDef,
	InitialTableState,
	PaginationState,
	SortingState,
	flexRender,
	getCoreRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	useReactTable,
} from "@tanstack/react-table";
import {
	CSSProperties,
	Dispatch,
	SetStateAction,
	useEffect,
	useMemo,
	useState,
} from "react";

import {
	ArrowsUpDownIcon,
	ChevronLeftIcon,
	ChevronRightIcon,
	ArrowUpIcon,
	ArrowDownIcon,
} from "@heroicons/react/24/outline";
import {
	Button,
	ButtonProps,
	Pagination,
	PaginationContent,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from ".";
import { cn } from "../../lib/utils";

// Refer https://tanstack.com/table/v8/docs/framework/react/examples/column-pinning-sticky, background color is custom
const getCommonPinningStyles = <T,>(
	column: Column<T>,
	isHeaderCell = false,
): CSSProperties => {
	const isPinned = column.getIsPinned();
	const background = isPinned
		? isHeaderCell
			? `linear-gradient(to ${isPinned}, rgba(243,244,246,0) 0%, rgba(243,244,246,0.6) 10%, rgba(243,244,246,1) 20%)`
			: `linear-gradient(to ${isPinned}, rgba(255,255,255,0) 0%, rgba(255,255,255,0.6) 10%, rgba(255,255,255,1) 20%)`
		: undefined;

	return {
		background,
		left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
		right: isPinned === "right" ? `${column.getAfter("right")}px` : undefined,
		opacity: isPinned ? 0.95 : 1,
		position: isPinned ? "sticky" : "relative",
		width: column.getSize(),
		zIndex: isPinned ? 1 : 0,
	};
};

interface DataTableProps<TData, TValue> {
	columns: ColumnDef<TData, TValue>[];
	data: TData[];
	onRowClick?: (row?: TData) => void;
	emptyDataText?: string;
	pageCount: number;
	rowCount: number;
	setPagination: Dispatch<SetStateAction<PaginationState>>;
	pagination: PaginationState;
	setSorting: Dispatch<SetStateAction<SortingState>>;
	sorting: SortingState;
	// 🤷 Adding this custom prop because autoResetPageIndex not working well with manual setPagination
	shouldResetPageIndex: boolean;
	pageSizeOptions?: number[];
	initialState?: InitialTableState;
	defaultColumn?: Partial<ColumnDef<TData, unknown>>;
	nonInteractive?: boolean;
	eventOnRowClick?: 'onMouseUp' | 'onClick';
	disableHeaderRowHover?: boolean;
}

/**
 * Server-side paginated and sorted data table.
 */
const DataTableServer = <TData, TValue>({
	columns,
	data,
	onRowClick = () => {},
	emptyDataText = "No results found",
	pageCount,
	rowCount,
	pagination,
	setPagination,
	sorting,
	setSorting,
	shouldResetPageIndex,
	pageSizeOptions = [10, 20, 30, 40, 50],
	initialState,
	defaultColumn,
	nonInteractive = false,
	eventOnRowClick = 'onMouseUp',
	disableHeaderRowHover = false,
}: DataTableProps<TData, TValue>) => {
	const [isDragging, setIsDragging] = useState(false);
	const table = useReactTable({
		data,
		columnResizeMode: "onChange",
		columns,
		defaultColumn: {
			enableResizing: false,
			...defaultColumn,
		},
		state: {
			sorting,
			pagination,
		},
		initialState,
		// * Not using `autoResetPageIndex` because encounter weird behavior when setting pagination
		// autoResetPageIndex: true,
		// * Turn on manual setting
		manualSorting: true,
		manualPagination: true,
		// * Pass pagination data
		rowCount: rowCount,
		pageCount: pageCount,
		getCoreRowModel: getCoreRowModel(),
		onSortingChange: setSorting,
		getSortedRowModel: getSortedRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		onPaginationChange: setPagination,
	});

	const columnSizeVars = useMemo(() => {
		const headers = table.getFlatHeaders();
		const colSizes: { [key: string]: number } = {};
		for (let i = 0; i < headers.length; i++) {
			const header = headers[i]!;
			colSizes[`--header-${header.id}-size`] = header.getSize();
			colSizes[`--col-${header.column.id}-size`] = header.column.getSize();
		}
		return colSizes;
	}, [table.getState().columnSizingInfo, table.getState().columnSizing]);

	useEffect(() => {
		if (shouldResetPageIndex) {
			setPagination({ ...pagination, pageIndex: 0 });
			return;
		}
		const lastPageIndex = pageCount === 0 ? 0 : pageCount - 1;
		if (pagination.pageIndex > lastPageIndex) {
			setPagination({ ...pagination, pageIndex: lastPageIndex });
		}
	}, [shouldResetPageIndex, pageCount, pagination, setPagination]);

	const renderPaginationLabel = () => {
		if (data.length === 0) {
			return (
				<span className="flex items-center">
					<strong>0&nbsp;-&nbsp;0</strong>
					&nbsp;of&nbsp;
					<strong>0</strong>
				</span>
			);
		}
		return (
			<span className="flex items-center">
				<strong>
					{table.getState().pagination.pageIndex * pagination.pageSize + 1}
					&nbsp;-&nbsp;
					{table.getState().pagination.pageIndex * pagination.pageSize +
						table.getRowModel().rows.length}
				</strong>
				&nbsp;of&nbsp;
				<strong>{rowCount}</strong>
			</span>
		);
	};

	const renderPaginationControls = () => {
		// Inspired by https://mui.com/material-ui/react-pagination/
		let content = null;
		// Scenario 1:
		// < 1 2 3 4 5 6 7 >
		// total < 8
		if (pageCount < 8) {
			content = (
				<>
					{new Array(pageCount).fill(0).map((_, index) => (
						<PaginationButton
							variant="secondary"
							key={index}
							isActive={index === pagination.pageIndex}
							onClick={() => {
								setPagination({
									...pagination,
									pageIndex: index,
								});
							}}
						>
							{index + 1}
						</PaginationButton>
					))}
				</>
			);
		}

		// Scenario 2:
		// < 1 2 3 4 5 ... 10 >
		// abs(pageIndex - startIndex) < 4
		else if (pagination.pageIndex < 4) {
			content = (
				<>
					<PaginationButton
						variant="secondary"
						isActive={pagination.pageIndex + 1 === 1}
						onClick={() => setPagination({ ...pagination, pageIndex: 0 })}
					>
						1
					</PaginationButton>
					<PaginationButton
						variant="secondary"
						isActive={pagination.pageIndex + 1 === 2}
						onClick={() => setPagination({ ...pagination, pageIndex: 1 })}
					>
						2
					</PaginationButton>
					<PaginationButton
						variant="secondary"
						isActive={pagination.pageIndex + 1 === 3}
						onClick={() => setPagination({ ...pagination, pageIndex: 2 })}
					>
						3
					</PaginationButton>
					<PaginationButton
						variant="secondary"
						isActive={pagination.pageIndex + 1 === 4}
						onClick={() => setPagination({ ...pagination, pageIndex: 3 })}
					>
						4
					</PaginationButton>
					<PaginationButton
						variant="secondary"
						onClick={() => setPagination({ ...pagination, pageIndex: 4 })}
					>
						5
					</PaginationButton>
					<span>...</span>
					<PaginationButton
						variant="secondary"
						onClick={() =>
							setPagination({ ...pagination, pageIndex: pageCount - 1 })
						}
					>
						{pageCount}
					</PaginationButton>
				</>
			);
		}

		// Scenario 3:
		// < 1 ... 6 7 8 9 10 >
		// abs(endIndex - pageIndex) < 4
		else if (pageCount - pagination.pageIndex < 4) {
			content = (
				<>
					<PaginationButton
						variant="secondary"
						onClick={() => setPagination({ ...pagination, pageIndex: 0 })}
					>
						1
					</PaginationButton>
					<span>...</span>
					<PaginationButton
						variant="secondary"
						onClick={() =>
							setPagination({ ...pagination, pageIndex: pageCount - 5 })
						}
					>
						{pageCount - 4}
					</PaginationButton>
					<PaginationButton
						variant="secondary"
						onClick={() =>
							setPagination({ ...pagination, pageIndex: pageCount - 4 })
						}
					>
						{pageCount - 3}
					</PaginationButton>
					<PaginationButton
						variant="secondary"
						isActive={pagination.pageIndex + 1 === pageCount - 2}
						onClick={() =>
							setPagination({ ...pagination, pageIndex: pageCount - 3 })
						}
					>
						{pageCount - 2}
					</PaginationButton>
					<PaginationButton
						variant="secondary"
						isActive={pagination.pageIndex + 1 === pageCount - 1}
						onClick={() =>
							setPagination({ ...pagination, pageIndex: pageCount - 2 })
						}
					>
						{pageCount - 1}
					</PaginationButton>
					<PaginationButton
						variant="secondary"
						isActive={pagination.pageIndex + 1 === pageCount}
						onClick={() =>
							setPagination({ ...pagination, pageIndex: pageCount - 1 })
						}
					>
						{pageCount}
					</PaginationButton>
				</>
			);
		} else {
			// Scenario 4:
			// < 1 ... 4 5 6 ... 10 >
			content = (
				<>
					<PaginationButton
						variant="secondary"
						onClick={() => setPagination({ ...pagination, pageIndex: 1 })}
					>
						1
					</PaginationButton>
					<span>...</span>
					<PaginationButton
						variant="secondary"
						onClick={() =>
							setPagination({
								...pagination,
								pageIndex: pagination.pageIndex - 1,
							})
						}
					>
						{pagination.pageIndex}
					</PaginationButton>
					<PaginationButton
						variant="secondary"
						isActive={true}
						onClick={() =>
							setPagination({ ...pagination, pageIndex: pagination.pageIndex })
						}
					>
						{pagination.pageIndex + 1}
					</PaginationButton>
					<PaginationButton
						variant="secondary"
						onClick={() =>
							setPagination({
								...pagination,
								pageIndex: pagination.pageIndex + 1,
							})
						}
					>
						{pagination.pageIndex + 2}
					</PaginationButton>
					<span>...</span>
					<PaginationButton
						variant="secondary"
						onClick={() =>
							setPagination({ ...pagination, pageIndex: pageCount - 1 })
						}
					>
						{pageCount}
					</PaginationButton>
				</>
			);
		}

		return (
			<PaginationContent className="flex items-center space-x-2">
				<PaginationButton
					variant="secondary"
					className={cn("p-3", {
						"disabled:bg-transparent disabled:text-gray-400":
							!table.getCanPreviousPage(),
					})}
					onClick={() => table.previousPage()}
					disabled={!table.getCanPreviousPage()}
				>
					<ChevronLeftIcon className="size-4" />
				</PaginationButton>
				{content}
				<PaginationButton
					variant="secondary"
					className={cn("p-3", {
						"disabled:bg-transparent disabled:text-gray-400":
							!table.getCanNextPage(),
					})}
					onClick={() => table.nextPage()}
					disabled={!table.getCanNextPage()}
				>
					<ChevronRightIcon className="size-4" />
				</PaginationButton>
			</PaginationContent>
		);
	};

	return (
		<div className="border-gray-100 border rounded-lg overflow-hidden">
			<Table
				className="min-w-full table-fixed"
				style={{ ...columnSizeVars, width: table.getTotalSize() }}
			>
				<TableHeader className="bg-gray-100">
					{table.getHeaderGroups().map((headerGroup) => (
						<TableRow nonInteractive={nonInteractive} key={headerGroup.id} disableHeaderRowHover={disableHeaderRowHover}>
							{headerGroup.headers.map((header) => {
								if (header.isPlaceholder) return null;
								const columnCanResize = header.column.getCanResize();

								return (
									<TableHead
										key={header.id}
										className={cn(
											"text-xs text-gray-600",
											{ "pr-1.5": columnCanResize },
											{
												"cursor-pointer select-none hover:text-alta-light-green":
													header.column.getCanSort(),
											},
											{ "text-alta-light-green": header.column.getIsSorted() },
										)}
										style={{
											...getCommonPinningStyles(header.column, true),
											width: `calc(var(--header-${header?.id}-size) * 1px)`,
										}}
									>
										<div className="flex items-center">
											<div
												className={cn(
													"flex w-full",
													{
														"justify-end":
															header.column.columnDef.meta?.align === "right",
													},
													{
														"justify-center":
															header.column.columnDef.meta?.align === "center",
													},
												)}
												onClick={header.column.getToggleSortingHandler()}
											>
												<span className={cn("truncate")}>
													{flexRender(
														header.column.columnDef.header,
														header.getContext(),
													)}
													{header.column.getCanSort() && (
														<>
															&nbsp;
															{header.column.getIsSorted() ? (
																header.column.getIsSorted() === "desc" ? (
																	<ArrowDownIcon className="inline-block size-3 stroke-2 mb-0.5" />
																) : (
																	<ArrowUpIcon className="inline-block size-3 stroke-2 mb-0.5" />
																)
															) : (
																<ArrowsUpDownIcon className="inline-block size-3 stroke-2 mb-0.5" />
															)}
														</>
													)}
												</span>
											</div>
											{columnCanResize && (
												<div
													className="relative h-4 w-2 cursor-col-resize select-none touch-none group z-10"
													onDoubleClick={() => header.column.resetSize()}
													onMouseDown={header.getResizeHandler()}
													onTouchStart={header.getResizeHandler()}
												>
													<div
														className={cn(
															"absolute top-0 left-1/2 transform -translate-x-1/2 h-4 w-0.5 bg-gray-300 group-hover:bg-blue-500",
															{
																"bg-blue-500": header.column.getIsResizing(),
															},
														)}
													/>
												</div>
											)}
										</div>
									</TableHead>
								);
							})}
						</TableRow>
					))}
				</TableHeader>
				<TableBody>
					{table.getRowModel().rows?.length ? (
						<>
							{table.getRowModel().rows.map((row) => (
								<TableRow
									nonInteractive={nonInteractive}
									onMouseDown={() => {
										if (!nonInteractive) setIsDragging(false);
									}}
									onMouseMove={() => {
										if (!nonInteractive && window.getSelection()?.toString()) {
											setIsDragging(true);
										}
									}}
									onMouseUp={(event) => {
										if (
											eventOnRowClick === 'onMouseUp' && 
											!nonInteractive &&
											!isDragging &&
											((event.target as HTMLElement).id === row.id ||
												(event.target as HTMLElement).getElementsByTagName(
													"div",
												).length > 0)
										) {
											onRowClick(row.original);
										}
										setIsDragging(false);
									}}
									onClick={(event) => {
										if (
											eventOnRowClick === 'onClick' && 
											((event.target as HTMLElement).id === row.id ||
												(event.target as HTMLElement).getElementsByTagName(
													"div",
												).length > 0)
										) {
											onRowClick(row.original);
										}
									}}
									key={row.id}
									data-state={row.getIsSelected() && "selected"}
								>
									{row.getVisibleCells().map((cell) => (
										<TableCell
											key={cell.id}
											align={cell.column.columnDef.meta?.align}
											// Reduce y-padding to show more rows in single page
											className={cn(
												"py-2",
												cell.column.columnDef.meta?.className,
											)}
											style={{
												...getCommonPinningStyles(cell.column),
												width: `calc(var(--col-${cell.column.id}-size) * 1px)`,
											}}
										>
											<div id={row.id} className={cn("truncate")}>
												{flexRender(
													cell.column.columnDef.cell,
													cell.getContext(),
												)}
											</div>
										</TableCell>
									))}
								</TableRow>
							))}
						</>
					) : (
						<TableRow nonInteractive={nonInteractive}>
							<TableCell colSpan={columns.length} className="py-4 text-center">
								{emptyDataText}
							</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>
			<Pagination className="justify-end space-x-4 bg-gray-50 px-4 py-2 text-sm">
				{renderPaginationLabel()}
				{renderPaginationControls()}
				<select
					value={table.getState().pagination.pageSize}
					className="rounded-lg bg-transparent px-2 hover:bg-gray-200"
					onChange={(e) => {
						setPagination({ pageIndex: 0, pageSize: Number(e.target.value) });
					}}
				>
					{pageSizeOptions.map((pageSize) => (
						<option key={pageSize} value={pageSize}>
							Show {pageSize}
						</option>
					))}
				</select>
			</Pagination>
		</div>
	);
};

interface PaginationButtonProps extends ButtonProps {
	/** @Default false */
	isActive?: boolean;
}

function PaginationButton(props: PaginationButtonProps) {
	const { isActive = false, className, ...rest } = props;
	return (
		<Button
			type="button"
			{...rest}
			className={cn(
				"hover:bg-gray-200 bg-transparent rounded-full",
				{
					"bg-gray-200": isActive,
				},
				className,
			)}
		/>
	);
}

export { DataTableServer };
